import { apiClient } from "@/lib/api";
import type {
  StandardResponse,
  PaginatedStandardResponse,
  PaginationInfo,
} from "@/types/api-response";
import { extractData, extractPaginatedData } from "@/types/api-response";
import type {
  ComponentPriceListDto,
  ComponentPriceDetailDto,
  CreateComponentPriceRequest,
  UpdateComponentPriceRequest,
  ComponentPriceFilters,
} from "@/types/component-price";

/**
 * Admin Component Price Service
 *
 * Handles all component price management API calls for the admin panel.
 * All responses follow StandardResponse<T> or PaginatedStandardResponse<T> format.
 */
export const adminComponentPriceService = {
  /**
   * Get paginated list of component prices with filters
   */
  async getComponentPrices(
    componentId: string,
    filters: ComponentPriceFilters,
  ): Promise<{ data: ComponentPriceListDto[]; pagination: PaginationInfo }> {
    const params = new URLSearchParams();

    if (filters.region) params.append("region", filters.region);
    if (filters.currency) params.append("currency", filters.currency);
    if (filters.billingFrequency)
      params.append("billingFrequency", filters.billingFrequency);
    if (filters.isActive !== undefined)
      params.append("isActive", filters.isActive.toString());
    if (filters.effectiveDate)
      params.append("effectiveDate", filters.effectiveDate);
    params.append("page", (filters.page ?? 1).toString());
    params.append("limit", (filters.limit ?? 20).toString());

    const response = await apiClient.get<
      PaginatedStandardResponse<ComponentPriceListDto>
    >(
      `/api/v1/admin/pricing/components/${componentId}/prices?${params.toString()}`,
    );

    return extractPaginatedData(response.data);
  },

  /**
   * Get detailed component price information by ID
   */
  async getComponentPriceById(
    priceId: string,
  ): Promise<ComponentPriceDetailDto> {
    const response = await apiClient.get<
      StandardResponse<ComponentPriceDetailDto>
    >(`/api/v1/admin/pricing/prices/${priceId}`);

    return extractData(response.data);
  },

  /**
   * Create new component price
   */
  async createComponentPrice(
    componentId: string,
    data: CreateComponentPriceRequest,
  ): Promise<ComponentPriceDetailDto> {
    const response = await apiClient.post<
      StandardResponse<ComponentPriceDetailDto>
    >(`/api/v1/admin/pricing/components/${componentId}/prices`, data);

    return extractData(response.data);
  },

  /**
   * Update component price details
   */
  async updateComponentPrice(
    priceId: string,
    data: UpdateComponentPriceRequest,
  ): Promise<ComponentPriceDetailDto> {
    const response = await apiClient.put<
      StandardResponse<ComponentPriceDetailDto>
    >(`/api/v1/admin/pricing/prices/${priceId}`, data);

    return extractData(response.data);
  },

  /**
   * Delete component price
   */
  async deleteComponentPrice(priceId: string): Promise<void> {
    await apiClient.delete(`/api/v1/admin/pricing/prices/${priceId}`);
  },
};
